// Enhanced PDF generation utilities for multi-page reports
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export class PDFGenerator {
  // PDF configuration constants
  static PDF_CONFIG = {
    format: 'a4',
    orientation: 'portrait',
    margins: {
      top: 15,
      right: 15,
      bottom: 20, // Extra space for footer
      left: 15
    },
    quality: 1.8, // Higher quality for better text rendering
    backgroundColor: '#ffffff'
  };

  /**
   * Download multi-page report as PDF with enhanced formatting
   */
  static async downloadReportPDF(generatedReportData, reportPreviewRef, onProgress = null) {
    if (!generatedReportData || !reportPreviewRef?.current) {
      console.warn('❌ Missing report data or preview reference for PDF generation');
      console.log('generatedReportData:', generatedReportData);
      console.log('reportPreviewRef?.current:', reportPreviewRef?.current);
      return;
    }
    
    try {
      console.log('🚀 Starting enhanced multi-page PDF generation...');
      console.log('Report data:', generatedReportData);
      console.log('Preview element:', reportPreviewRef.current);
      
      // Create new PDF document with enhanced settings
      const pdf = new jsPDF({
        orientation: this.PDF_CONFIG.orientation,
        unit: 'mm',
        format: this.PDF_CONFIG.format,
        compress: true
      });
      
      // Get PDF dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Find all page containers with better selectors
      const pageContainers = this.findPageContainers(reportPreviewRef.current);
      console.log('🔍 Found page containers:', pageContainers.length);
      console.log('Page containers:', pageContainers);
      
      if (pageContainers.length === 0) {
        console.error('❌ No page containers found for PDF generation');
        console.log('Available elements in preview:', reportPreviewRef.current.querySelectorAll('*'));
        throw new Error('No page containers found for PDF generation');
      }
      
      console.log(`📄 Processing ${pageContainers.length} pages for PDF...`);
      
      // Process each page
      for (let i = 0; i < pageContainers.length; i++) {
        const pageElement = pageContainers[i];
        const pageInfo = this.getPageInfo(pageElement, i, pageContainers.length);
        
        // Update progress
        if (onProgress) {
          onProgress({
            current: i + 1,
            total: pageContainers.length,
            status: `Capturing ${pageInfo.title}...`
          });
        }
        
        // Add new page (except for first page)
        if (i > 0) {
          pdf.addPage();
        }
        
        console.log(`📸 Capturing page ${i + 1}/${pageContainers.length}: ${pageInfo.title}`);
        
        // Enhanced page capture with fallback
        let canvas, imgData;
        try {
          canvas = await this.capturePageWithRetry(pageElement, pageInfo);
          imgData = canvas.toDataURL('image/png', 0.95);
        } catch (captureError) {
          console.warn(`⚠️ Advanced capture failed for page ${pageInfo.number}, trying fallback:`, captureError);
          
          // Fallback: ultra-simple html2canvas
          try {
            canvas = await new Promise((resolve, reject) => {
              html2canvas(pageElement, {
                scale: 1,
                backgroundColor: '#ffffff'
              }).then(resolve).catch(reject);
            });
            
            if (canvas && canvas.toDataURL) {
              imgData = canvas.toDataURL('image/png');
              console.log(`✅ Fallback capture successful for page ${pageInfo.number}`);
            } else {
              throw new Error('Fallback canvas is invalid');
            }
          } catch (fallbackError) {
            console.error(`❌ Both capture methods failed for page ${pageInfo.number}:`, fallbackError);
            throw new Error(`Cannot capture page ${pageInfo.number}: ${fallbackError.message}`);
          }
        }
        
        // Calculate optimal image dimensions
        const dimensions = this.calculateOptimalDimensions(
          canvas, 
          pdfWidth, 
          pdfHeight, 
          this.PDF_CONFIG.margins
        );
        
        // Add page content
        pdf.addImage(
          imgData, 
          'PNG', 
          dimensions.x, 
          dimensions.y, 
          dimensions.width, 
          dimensions.height,
          undefined,
          'FAST'
        );
        
        // Add headers and footers
        this.addPageHeaderFooter(pdf, pageInfo, generatedReportData, pdfWidth, pdfHeight);
      }
      
      // Add PDF metadata
      this.addPDFMetadata(pdf, generatedReportData);
      
      // Generate filename
      const fileName = this.generateFileName(generatedReportData);
      
      // Download the PDF
      pdf.save(fileName);
      
      console.log(`✅ Enhanced multi-page PDF downloaded successfully: ${fileName}`);
      
      if (onProgress) {
        onProgress({
          current: pageContainers.length,
          total: pageContainers.length,
          status: 'PDF download complete!'
        });
      }
      
    } catch (error) {
      console.error('❌ Error generating enhanced multi-page PDF:', error);
      
      // Enhanced fallback with better error handling
      await this.handlePDFGenerationError(error, generatedReportData, onProgress);
    }
  }

  /**
   * Find page containers with improved detection
   */
  static findPageContainers(previewElement) {
    // Try multiple selectors for page detection
    const selectors = [
      '.page-content', 
      '[class*="page-content"]',
      '.cover-page',
      '[style*="297mm"]', // A4 height containers
      '.report-page'
    ];
    
    for (const selector of selectors) {
      const containers = previewElement.querySelectorAll(selector);
      if (containers.length > 0) {
        console.log(`📄 Found ${containers.length} page containers using selector: ${selector}`);
        return Array.from(containers);
      }
    }
    
    // Fallback: use the main preview element
    console.log('⚠️ No page containers found, using main preview element');
    return [previewElement];
  }

  /**
   * Get page information for headers/footers
   */
  static getPageInfo(pageElement, index, totalPages) {
    // Try to extract page title from various sources
    let title = 'Page ' + (index + 1);
    
    // Look for page titles in headers
    const titleSelectors = [
      'h1', 'h2', '.page-title', '[data-page-title]'
    ];
    
    for (const selector of titleSelectors) {
      const titleElement = pageElement.querySelector(selector);
      if (titleElement && titleElement.textContent.trim()) {
        title = titleElement.textContent.trim();
        break;
      }
    }
    
    return {
      index: index,
      number: index + 1,
      total: totalPages,
      title: title,
      isFirstPage: index === 0,
      isLastPage: index === totalPages - 1
    };
  }

  /**
   * Capture page with retry mechanism for better reliability
   */
  static async capturePageWithRetry(pageElement, pageInfo, maxRetries = 2) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Ensure page is fully visible and rendered
        pageElement.scrollIntoView({ behavior: 'instant', block: 'start' });
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait for rendering
        
        console.log(`🎯 Attempting html2canvas for page ${pageInfo.number}...`);
        console.log('Page element:', pageElement);
        console.log('Element dimensions:', pageElement.scrollWidth, 'x', pageElement.scrollHeight);
        
        // Create canvas with robust error handling and compatibility fixes
        const canvas = await new Promise((resolve, reject) => {
          html2canvas(pageElement, {
            scale: 1.2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: this.PDF_CONFIG.backgroundColor,
            width: pageElement.scrollWidth,
            height: pageElement.scrollHeight,
            logging: false,
            onclone: (clonedDoc) => {
              try {
                const navElements = clonedDoc.querySelectorAll('.page-navigation, .no-print, nav, button');
                navElements.forEach(el => {
                  if (el) el.style.display = 'none';
                });
              } catch (cloneError) {
                console.warn('Error in onclone:', cloneError);
              }
            }
          }).then((canvasResult) => {
            console.log(`🔍 html2canvas returned:`, canvasResult);
            console.log(`🔍 canvas type:`, typeof canvasResult);
            console.log(`🔍 canvas constructor:`, canvasResult?.constructor?.name);
            console.log(`🔍 canvas dimensions:`, canvasResult?.width, 'x', canvasResult?.height);

            if (!canvasResult) {
              reject(new Error('html2canvas returned null'));
              return;
            }

            // CRITICAL FIX: Check if it's actually a canvas element
            let actualCanvas = canvasResult;
            
            // If the result is wrapped or not a proper canvas, try to extract it
            if (canvasResult.nodeName !== 'CANVAS') {
              console.log('🔧 Result is not a canvas element, checking for canvas property...');
              if (canvasResult.canvas && canvasResult.canvas.nodeName === 'CANVAS') {
                actualCanvas = canvasResult.canvas;
                console.log('✅ Found canvas in .canvas property');
              } else if (canvasResult._canvas && canvasResult._canvas.nodeName === 'CANVAS') {
                actualCanvas = canvasResult._canvas;
                console.log('✅ Found canvas in ._canvas property');
              } else {
                console.error('❌ Cannot find canvas element in result:', Object.keys(canvasResult));
                reject(new Error('html2canvas result is not a canvas element'));
                return;
              }
            }

            // Final validation (FIXED: check for toDataURL instead of getDataURL)
            if (!actualCanvas || !actualCanvas.toDataURL) {
              console.error('❌ Final canvas validation failed:', actualCanvas);
              reject(new Error('Final canvas does not have toDataURL method'));
              return;
            }

            // Test toDataURL (FIXED: was using wrong method name)
            try {
              const testData = actualCanvas.toDataURL('image/png', 0.5);
              if (!testData || !testData.startsWith('data:image/')) {
                reject(new Error('toDataURL returned invalid data'));
                return;
              }
              console.log(`✅ Canvas validation successful for page ${pageInfo.number}`);
              resolve(actualCanvas);
            } catch (testError) {
              console.error('❌ toDataURL test error:', testError);
              reject(new Error(`toDataURL failed: ${testError.message}`));
            }
          }).catch((error) => {
            console.error('❌ html2canvas error:', error);
            reject(error);
          });
        });
        
        console.log(`✅ Page ${pageInfo.number} captured successfully (${canvas.width}x${canvas.height})`);
        return canvas;
        
      } catch (error) {
        console.warn(`⚠️ Attempt ${attempt} failed for page ${pageInfo.number}:`, error);
        if (attempt === maxRetries) {
          throw error;
        }
        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  /**
   * Calculate optimal image dimensions for PDF
   */
  static calculateOptimalDimensions(canvas, pdfWidth, pdfHeight, margins) {
    const availableWidth = pdfWidth - (margins.left + margins.right);
    const availableHeight = pdfHeight - (margins.top + margins.bottom);
    
    // Calculate aspect ratio
    const canvasRatio = canvas.width / canvas.height;
    const availableRatio = availableWidth / availableHeight;
    
    let width, height;
    
    if (canvasRatio > availableRatio) {
      // Canvas is wider - fit to width
      width = availableWidth;
      height = availableWidth / canvasRatio;
    } else {
      // Canvas is taller - fit to height
      height = availableHeight;
      width = availableHeight * canvasRatio;
    }
    
    // Center the image
    const x = margins.left + (availableWidth - width) / 2;
    const y = margins.top + (availableHeight - height) / 2;
    
    return { x, y, width, height };
  }

  /**
   * Add professional headers and footers
   */
  static addPageHeaderFooter(pdf, pageInfo, reportData, pdfWidth, pdfHeight) {
    const { margins } = this.PDF_CONFIG;
    
    // Set font for headers/footers
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);
    
    // Header (only on non-cover pages)
    if (!pageInfo.isFirstPage) {
      const headerText = reportData?.title || 'Construction Progress Report';
      pdf.text(headerText, margins.left, margins.top - 5);
      
      // Header line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.1);
      pdf.line(margins.left, margins.top - 2, pdfWidth - margins.right, margins.top - 2);
    }
    
    // Footer
    const footerY = pdfHeight - margins.bottom + 15;
    
    // Page number (center)
    const pageText = `Page ${pageInfo.number} of ${pageInfo.total}`;
    const pageTextWidth = pdf.getTextWidth(pageText);
    pdf.text(pageText, (pdfWidth - pageTextWidth) / 2, footerY);
    
    // Generated date (left)
    const dateText = `Generated: ${new Date().toLocaleDateString()}`;
    pdf.text(dateText, margins.left, footerY);
    
    // Company name (right)
    const companyText = 'SiteOrbit Construction Management';
    const companyTextWidth = pdf.getTextWidth(companyText);
    pdf.text(companyText, pdfWidth - margins.right - companyTextWidth, footerY);
    
    // Footer line
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.1);
    pdf.line(margins.left, footerY - 3, pdfWidth - margins.right, footerY - 3);
  }

  /**
   * Add PDF metadata
   */
  static addPDFMetadata(pdf, reportData) {
    const metadata = {
      title: reportData?.title || 'Construction Progress Report',
      subject: 'Construction Progress Report generated by SiteOrbit',
      author: 'SiteOrbit Construction Management System',
      creator: 'SiteOrbit PDF Generator v2.0',
      producer: 'SiteOrbit Construction Management System',
      keywords: 'construction, progress, report, SiteOrbit'
    };
    
    // Set PDF metadata
    Object.entries(metadata).forEach(([key, value]) => {
      if (value) {
        pdf.setDocumentProperties({ [key]: value });
      }
    });
  }

  /**
   * Generate smart filename
   */
  static generateFileName(reportData) {
    const title = (reportData?.title || 'construction_progress_report')
      .replace(/[^a-z0-9\s]/gi, '')
      .replace(/\s+/g, '_')
      .toLowerCase();
    
    const date = new Date().toISOString().split('T')[0];
    const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '');
    
    return `${title}_${date}_${time}.pdf`;
  }

  /**
   * Handle PDF generation errors with enhanced fallback
   */
  static async handlePDFGenerationError(error, reportData, onProgress = null) {
    console.error('❌ Enhanced PDF generation failed:', error);
    
    if (onProgress) {
      onProgress({
        current: 0,
        total: 1,
        status: 'PDF generation failed, creating text fallback...'
      });
    }
    
    try {
      console.log('📄 Attempting enhanced fallback text generation...');
      const reportContent = this.generateEnhancedReportText(reportData);
      
      // Create and download text file
      const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.generateFileName(reportData).replace('.pdf', '_fallback.txt');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('✅ Enhanced fallback text download completed');
      
      if (onProgress) {
        onProgress({
          current: 1,
          total: 1,
          status: 'Fallback text file downloaded successfully!'
        });
      }
      
    } catch (fallbackError) {
      console.error('❌ Enhanced fallback text generation failed:', fallbackError);
      throw new Error('Failed to generate both PDF and text versions of the report');
    }
  }

  /**
   * Generate enhanced formatted report text content as fallback
   */
  static generateEnhancedReportText(generatedReportData) {
    if (!generatedReportData) return 'No report data available';
    
    const { title, sections, dateRange, generatedAt, photos } = generatedReportData;
    const currentDate = new Date().toLocaleString();
    
    return `
╔═══════════════════════════════════════════════════════════════════════════════╗
║                    CONSTRUCTION PROGRESS REPORT                               ║
║                     Generated by SiteOrbit System                             ║
╚═══════════════════════════════════════════════════════════════════════════════╝

📊 REPORT INFORMATION
${'-'.repeat(80)}
Report Title: ${title || 'Untitled Construction Progress Report'}
Generated: ${new Date(generatedAt || Date.now()).toLocaleString()}
Report Period: ${dateRange?.startDate || 'N/A'} to ${dateRange?.endDate || 'N/A'}
Export Date: ${currentDate}
Document Version: 2.0 (Enhanced Multi-Page Format)

📋 EXECUTIVE SUMMARY
${'-'.repeat(80)}
${this.formatTextSection('Project Overview', sections?.executiveSummary?.overview)}
${this.formatTextSection('Key Performance Metrics', sections?.executiveSummary?.keyMetrics)}
${this.formatTextSection('Major Achievements', sections?.executiveSummary?.majorAchievements)}
${this.formatTextSection('Challenges & Issues', sections?.executiveSummary?.challenges)}
${this.formatTextSection('Recommendations', sections?.executiveSummary?.recommendations)}

🏗️ PROJECT OVERVIEW & SCOPE
${'-'.repeat(80)}
${this.formatTextSection('Project Description', sections?.projectOverview?.projectDescription)}
${this.formatTextSection('Scope of Work', sections?.projectOverview?.scope)}
${this.formatTextSection('Project Objectives', sections?.projectOverview?.objectives)}
${this.formatTextSection('Project Timeline', sections?.projectOverview?.timeline)}
${this.formatTextSection('Key Stakeholders', sections?.projectOverview?.stakeholders)}

⭐ PROGRESS HIGHLIGHTS
${'-'.repeat(80)}
${this.formatTextSection('Completed Milestones', sections?.progressHighlights?.completedMilestones)}
${this.formatTextSection('Current Activities', sections?.progressHighlights?.currentActivities)}
${this.formatTextSection('Upcoming Work', sections?.progressHighlights?.upcomingWork)}
${this.formatTextSection('Project Completion Status', sections?.progressHighlights?.percentComplete ? `${sections.progressHighlights.percentComplete} Complete` : null)}

✅ WORK COMPLETED THIS PERIOD
${'-'.repeat(80)}
${this.formatTextSection('Activities Completed', sections?.workCompleted?.activities)}
${this.formatTextSection('Key Deliverables', sections?.workCompleted?.deliverables)}
${this.formatTextSection('Labor Hours', sections?.workCompleted?.manHours)}
${this.formatTextSection('Materials Used', sections?.workCompleted?.materials)}
${this.formatTextSection('Equipment Utilized', sections?.workCompleted?.equipment)}

🔍 QUALITY INSPECTIONS & STANDARDS
${'-'.repeat(80)}
${this.formatTextSection('Quality Inspections', sections?.qualityInspections?.inspections)}
${this.formatTextSection('Standards Compliance', sections?.qualityInspections?.standards)}
${this.formatTextSection('Quality Compliance', sections?.qualityInspections?.compliance)}
${this.formatTextSection('Identified Defects', sections?.qualityInspections?.defects)}
${this.formatTextSection('Corrective Actions', sections?.qualityInspections?.corrective)}

🛡️ SAFETY COMPLIANCE & INCIDENTS
${'-'.repeat(80)}
${this.formatTextSection('Safety Incidents', sections?.safetyCompliance?.incidents)}
${this.formatTextSection('Safety Measures Implemented', sections?.safetyCompliance?.measures)}
${this.formatTextSection('Safety Training', sections?.safetyCompliance?.training)}
${this.formatTextSection('Safety Compliance Status', sections?.safetyCompliance?.compliance)}
${this.formatTextSection('Safety Improvements', sections?.safetyCompliance?.improvements)}

📸 PHOTO DOCUMENTATION
${'-'.repeat(80)}
${this.formatPhotoSection(photos)}

🎯 KEY ACHIEVEMENTS & MILESTONES
${'-'.repeat(80)}
${this.formatTextSection('Project Milestones', sections?.keyAchievements?.milestones)}
${this.formatTextSection('Awards & Recognition', sections?.keyAchievements?.awards)}
${this.formatTextSection('Innovations Implemented', sections?.keyAchievements?.innovations)}
${this.formatTextSection('Project Successes', sections?.keyAchievements?.successes)}

📁 APPENDICES & DOCUMENTATION
${'-'.repeat(80)}
${this.formatTextSection('Supporting Documents', sections?.appendices?.documents)}
${this.formatTextSection('References', sections?.appendices?.references)}
${this.formatTextSection('Technical Calculations', sections?.appendices?.calculations)}
${this.formatTextSection('Certifications', sections?.appendices?.certifications)}

${'-'.repeat(80)}
📊 Report Statistics:
   • Total Sections: ${Object.keys(sections || {}).length}
   • Photos Included: ${photos?.length || 0}
   • Generation Time: ${currentDate}
   • Format: Enhanced Text Export (PDF Generation Fallback)

🏢 Generated by SiteOrbit Construction Management System v2.0
   Professional Construction Reporting & Project Management
   
   For technical support: support@webmail.siteorbit.app
   Website: siteorbit.app
${'-'.repeat(80)}
`;
  }

  /**
   * Format individual text sections
   */
  static formatTextSection(title, content) {
    if (!content || content.trim() === '') {
      return `\n${title}:\n   [No information provided]\n`;
    }
    
    return `\n${title}:\n${content.split('\n').map(line => `   ${line}`).join('\n')}\n`;
  }

  /**
   * Format photo documentation section
   */
  static formatPhotoSection(photos) {
    if (!photos || photos.length === 0) {
      return '\n   [No photos included in this report]\n';
    }
    
    let photoText = `\n   Total Photos Documented: ${photos.length}\n\n`;
    
    photos.forEach((photo, index) => {
      photoText += `   📷 Photo ${index + 1}: ${photo.label || 'Untitled'}\n`;
      if (photo.description) {
        photoText += `      Description: ${photo.description}\n`;
      }
      photoText += `      Added: ${new Date().toLocaleDateString()}\n\n`;
    });
    
    return photoText;
  }

  /**
   * Generate formatted report text content as fallback (legacy method)
   */
  static generateReportText(generatedReportData) {
    if (!generatedReportData) return '';
    
    const { title, sections, dateRange, generatedAt } = generatedReportData;
    
    return `
COMPREHENSIVE CONSTRUCTION PROGRESS REPORT
==========================================

Report Title: ${title}
Generated: ${new Date(generatedAt).toLocaleString()}
Report Period: ${dateRange?.startDate || 'N/A'} to ${dateRange?.endDate || 'N/A'}

EXECUTIVE SUMMARY
-----------------
${sections?.executiveSummary?.overview || 'No executive summary provided'}

Key Metrics:
${sections?.executiveSummary?.keyMetrics || 'No metrics provided'}

Major Achievements:
${sections?.executiveSummary?.majorAchievements || 'No achievements recorded'}

Challenges:
${sections?.executiveSummary?.challenges || 'No challenges recorded'}

Recommendations:
${sections?.executiveSummary?.recommendations || 'No recommendations provided'}

PROJECT OVERVIEW & SCOPE
-------------------------
Project Description:
${sections?.projectOverview?.projectDescription || 'No project description provided'}

Scope:
${sections?.projectOverview?.scope || 'No scope defined'}

Objectives:
${sections?.projectOverview?.objectives || 'No objectives defined'}

Timeline:
${sections?.projectOverview?.timeline || 'No timeline provided'}

Stakeholders:
${sections?.projectOverview?.stakeholders || 'No stakeholders listed'}

PROGRESS HIGHLIGHTS
-------------------
Completed Milestones:
${sections?.progressHighlights?.completedMilestones || 'No milestones completed'}

Current Activities:
${sections?.progressHighlights?.currentActivities || 'No current activities'}

Upcoming Work:
${sections?.progressHighlights?.upcomingWork || 'No upcoming work planned'}

Percent Complete:
${sections?.progressHighlights?.percentComplete || 'Not specified'}

WORK COMPLETED THIS PERIOD
---------------------------
Activities:
${sections?.workCompleted?.activities || 'No activities recorded'}

Deliverables:
${sections?.workCompleted?.deliverables || 'No deliverables recorded'}

Man Hours:
${sections?.workCompleted?.manHours || 'No hours recorded'}

Materials:
${sections?.workCompleted?.materials || 'No materials recorded'}

Equipment:
${sections?.workCompleted?.equipment || 'No equipment recorded'}

QUALITY INSPECTIONS & STANDARDS
--------------------------------
Inspections:
${sections?.qualityInspections?.inspections || 'No inspections recorded'}

Standards:
${sections?.qualityInspections?.standards || 'No standards specified'}

Compliance:
${sections?.qualityInspections?.compliance || 'No compliance information'}

Defects:
${sections?.qualityInspections?.defects || 'No defects recorded'}

Corrective Actions:
${sections?.qualityInspections?.corrective || 'No corrective actions recorded'}

SAFETY COMPLIANCE & INCIDENTS
------------------------------
Incidents:
${sections?.safetyCompliance?.incidents || 'No incidents to report'}

Safety Measures:
${sections?.safetyCompliance?.measures || 'No measures recorded'}

Training:
${sections?.safetyCompliance?.training || 'No training recorded'}

Compliance:
${sections?.safetyCompliance?.compliance || 'No compliance information'}

Improvements:
${sections?.safetyCompliance?.improvements || 'No improvements recorded'}

PHOTO DOCUMENTATION
-------------------
${generatedReportData.photos && generatedReportData.photos.length > 0 
  ? `Total Photos: ${generatedReportData.photos.length}\n\n${generatedReportData.photos.map((photo, index) => 
      `Photo ${index + 1}: ${photo.label || 'Untitled'}\n${photo.description || 'No description'}\n`
    ).join('\n')}`
  : 'No photos included in this report'
}

KEY ACHIEVEMENTS & MILESTONES
------------------------------
${sections?.keyAchievements?.milestones || 'No achievements recorded'}

Awards:
${sections?.keyAchievements?.awards || 'No awards received'}

Innovations:
${sections?.keyAchievements?.innovations || 'No innovations implemented'}

Successes:
${sections?.keyAchievements?.successes || 'No successes recorded'}

APPENDICES & ADDITIONAL DOCUMENTATION
--------------------------------------
${sections?.appendices?.documents || 'No additional documentation'}

References:
${sections?.appendices?.references || 'No references provided'}

Calculations:
${sections?.appendices?.calculations || 'No calculations provided'}

Certifications:
${sections?.appendices?.certifications || 'No certifications provided'}

----------------------------------------
Report generated by SiteOrbit Construction Management System
Generated on ${new Date().toLocaleString()}
`;
  }

  /**
   * Generate report preview for inline viewing
   */
  static generatePreviewHTML(generatedReportData, reportSections) {
    // This could be used to generate HTML preview content
    // For now, returning basic structure
    return {
      title: generatedReportData?.title || 'Untitled Report',
      sections: reportSections || {},
      generatedAt: generatedReportData?.generatedAt || new Date().toISOString()
    };
  }
}